import React, { useRef, useEffect, useState, ReactNode } from 'react';
import styles from './styles.module.scss';

interface DoubleScrollbarProps {
  children: ReactNode;
  fillHeight?: boolean;
  onContentScroll?: (scrollLeft: number) => void;
}

interface ScrollableElement extends HTMLDivElement {
  isScrolling?: boolean;
}

/**
 * Renders a container with two synchronized horizontal scrollbars:
 * one at the top and one at the bottom (native to the content).
 * Useful for wide content (like tables) where the top scrollbar
 * improves usability when the bottom scrollbar is out of view.
 */
export const DoubleScrollbar = ({ children, fillHeight = false, onContentScroll }: DoubleScrollbarProps) => {
  const topScrollbarRef = useRef<ScrollableElement>(null);
  const contentContainerRef = useRef<ScrollableElement>(null);
  const dummyContentRef = useRef<HTMLDivElement>(null);
  const [contentWidth, setContentWidth] = useState<number>(0);

  useEffect(() => {
    const updateContentWidth = (): void => {
      if (contentContainerRef.current) {
        const scrollWidth = contentContainerRef.current.scrollWidth;
        setContentWidth(scrollWidth);

        if (dummyContentRef.current) {
          dummyContentRef.current.style.width = `${scrollWidth}px`;
        }
      }
    };

    // Initial width calculation
    updateContentWidth();

    // Setup ResizeObserver to watch for content size changes
    const resizeObserver = new ResizeObserver(updateContentWidth);
    if (contentContainerRef.current) {
      resizeObserver.observe(contentContainerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [children]);

  // Handle scroll events to synchronize the scroll positions of both scrollbars
  const handleScroll = (e: React.UIEvent<HTMLDivElement>, ref: React.RefObject<ScrollableElement | null>): void => {
    const target = e.target as HTMLDivElement;
    if (ref.current && !ref.current.isScrolling) {
      ref.current.isScrolling = true;
      ref.current.scrollLeft = target.scrollLeft;
      setTimeout(() => {
        if (ref.current) {
          ref.current.isScrolling = false;
        }
      }, 0);
    }
  };

  const content = (
    <>
      <div className={styles.topScrollbar} ref={topScrollbarRef} onScroll={(e) => handleScroll(e, contentContainerRef)}>
        <div ref={dummyContentRef} className={styles.dummyContent} style={{ width: contentWidth }} />
      </div>
      <div
        className={`${styles.contentContainer}${fillHeight ? ` ${styles.fillHeight}` : ''}`}
        ref={contentContainerRef}
        onScroll={(e) => {
          handleScroll(e, topScrollbarRef);
          onContentScroll?.((e.target as HTMLDivElement).scrollLeft);
        }}
      >
        {children}
      </div>
    </>
  );

  return fillHeight ? <div className={styles.wrapper}>{content}</div> : content;
};
